import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EconsultUtils, ProgramSpecialty } from '@oh-vcp/components-common';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import useSpecialtyLookup from '../services/useSpecialtyLookup';
import useSubSpecialtyLookup from '../services/useSubSpecialtyLookup';

const SpecialtySelect = styled(Select<string>)({
  minWidth: 200,
  height: 40
});
const vaildFieldMessageType = {
  display: 'none'
};
const inVaildFieldMessageType = {
  display: 'block',
  color: 'red',
  fontSize: '0.75rem'
};
export interface EconsultCaseSpecialtySectionRef {
  validate: () => boolean;
}

export interface EconsultCaseSpecialtySectionProps {
  onSpecialtyChange?: () => void;
  prioritySpecialty: ProgramSpecialty | undefined;
  prioritySubSpecialty: ProgramSpecialty | undefined;
  onPrioritySpecialtyChanged: (specialty: ProgramSpecialty | undefined) => void;
  onPrioritySubSpecialtyChanged: (subSpecialty: ProgramSpecialty | undefined) => void;
}

const EconsultCaseSpecialtySection = forwardRef<
  EconsultCaseSpecialtySectionRef,
  EconsultCaseSpecialtySectionProps
>((props, ref) => {
  const {
    onSpecialtyChange,
    prioritySpecialty,
    prioritySubSpecialty,
    onPrioritySpecialtyChanged,
    onPrioritySubSpecialtyChanged
  } = props;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { caseDetails, setCaseDetails } = useEconsultCaseDetailsContext();
  const { data: fetchedProgramSpecialties } = useSpecialtyLookup(caseDetails?.referrer?.userId);
  const { data: fetchedSubSpecialties } = useSubSpecialtyLookup(
    caseDetails?.referrer?.userId,
    caseDetails?.specialty?.code
  );
  const [programSpecialties, setProgramSpecialties] = useState<ProgramSpecialty[]>([]);
  const [programSubSpecialties, setProgramSubSpecialties] = useState<ProgramSpecialty[]>([]);

  const [specialtyRequiredErrorMessageStyle, setSpecialtyRequiredErrorMessageStyle] =
    useState(vaildFieldMessageType);
  const [subSpecialtyRequiredErrorMessageStyle, setSubSpecialtyRequiredErrorMessageStyle] =
    useState(vaildFieldMessageType);

  useEffect(() => {
    const updatedSpecialties = [...(fetchedProgramSpecialties || [])];
    const updatedSubSpecialties = [...(fetchedSubSpecialties || [])];

    if (prioritySpecialty && prioritySubSpecialty) {
      const existingSpecialty = updatedSpecialties?.find((s) => s.code === prioritySpecialty.code);
      const existingSubSpecialty = updatedSubSpecialties?.find(
        (s) => s.subCode === prioritySubSpecialty.subCode
      );
      if (!existingSpecialty) {
        updatedSpecialties?.push(prioritySpecialty);
      }
      if (!existingSubSpecialty && caseDetails?.specialty?.code === prioritySubSpecialty?.code) {
        updatedSubSpecialties?.push(prioritySubSpecialty);
      }
    }
    setProgramSpecialties(updatedSpecialties);
    setProgramSubSpecialties(updatedSubSpecialties);
  }, [fetchedProgramSpecialties, fetchedSubSpecialties, prioritySpecialty, prioritySubSpecialty]);

  useEffect(() => {}, []);

  useEffect(() => {
    if (caseDetails?.specialty && caseDetails?.subSpecialty) {
      if (!prioritySpecialty && onPrioritySpecialtyChanged) {
        onPrioritySpecialtyChanged(caseDetails?.specialty);
      }
      if (!prioritySubSpecialty && onPrioritySubSpecialtyChanged) {
        onPrioritySubSpecialtyChanged(caseDetails?.subSpecialty);
      }
    }
  }, [caseDetails]);

  useImperativeHandle(ref, () => ({
    validate() {
      const specialtyValid = caseDetails?.specialty && caseDetails?.specialty?.code;
      if (specialtyValid) {
        setSpecialtyRequiredErrorMessageStyle(vaildFieldMessageType);
        if (programSubSpecialties) {
          const subSpecialtyValid = caseDetails?.subSpecialty && caseDetails?.subSpecialty?.subCode;
          if (subSpecialtyValid) {
            setSubSpecialtyRequiredErrorMessageStyle(vaildFieldMessageType);
            return true;
          }
          setSubSpecialtyRequiredErrorMessageStyle(inVaildFieldMessageType);
          return false;
        }
        return true;
      }
      setSpecialtyRequiredErrorMessageStyle(inVaildFieldMessageType);
      return false;
    }
  }));

  if (!programSpecialties) {
    return null;
  }
  const handleSpecialtyChanged = (event: SelectChangeEvent) => {
    const specialtyCode = event.target.value;
    const selectedSpecialty = programSpecialties.find((s) => s.code === specialtyCode);
    const updatedCaseDetails = {
      ...caseDetails,
      specialty: selectedSpecialty,
      subSpecialty: undefined
    };
    setCaseDetails(updatedCaseDetails);
    setSpecialtyRequiredErrorMessageStyle(vaildFieldMessageType);
    if (onSpecialtyChange) {
      onSpecialtyChange();
    }
  };
  const handleSubSpecialtyChanged = (event: SelectChangeEvent) => {
    if (!programSubSpecialties) {
      return;
    }
    const subSpecialtyCode = event.target.value;
    const selectedSubSpecialty = programSubSpecialties.find((s) => s.subCode === subSpecialtyCode);
    const updatedCaseDetails = {
      ...caseDetails,
      subSpecialty: selectedSubSpecialty
    };
    setCaseDetails(updatedCaseDetails);
    setSubSpecialtyRequiredErrorMessageStyle(vaildFieldMessageType);
    if (onSpecialtyChange) {
      onSpecialtyChange();
    }
  };
  return (
    <Box>
      <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
        <Box>
          <SpecialtySelect
            onChange={handleSpecialtyChanged}
            value={caseDetails?.specialty?.code ?? '0'}>
            <MenuItem
              value="0"
              sx={{
                '&.Mui-selected:hover': { color: 'black' }
              }}>
              <Typography variant="body2">{t('Case.draft.selectCategory')}</Typography>
            </MenuItem>
            {EconsultUtils.sortProgramSpecialties(programSpecialties).map((specialty) => (
              <MenuItem
                key={specialty.code}
                value={specialty.code}
                sx={{
                  '&.Mui-selected:hover': { color: 'black' }
                }}>
                <Typography variant="body2">{specialty.description}</Typography>
              </MenuItem>
            ))}
          </SpecialtySelect>
          <Typography
            id="requestSpecialtyValidationdMessage"
            style={specialtyRequiredErrorMessageStyle}>
            {t('Case.patient.field.required')}
          </Typography>
        </Box>
        {programSubSpecialties && (
          <Box>
            <SpecialtySelect
              onChange={handleSubSpecialtyChanged}
              value={caseDetails?.subSpecialty?.subCode ?? '0'}>
              <MenuItem
                value="0"
                sx={{
                  '&.Mui-selected:hover': { color: 'black' }
                }}>
                <Typography variant="body2">{t('Case.draft.selectOption')}</Typography>
              </MenuItem>
              {EconsultUtils.sortProgramSpecialties(programSubSpecialties).map(
                (specialty, index) => (
                  <MenuItem
                    key={index}
                    value={specialty.subCode}
                    sx={{
                      '&.Mui-selected:hover': { color: 'black' }
                    }}>
                    <Typography variant="body2">{specialty.description}</Typography>
                  </MenuItem>
                )
              )}
            </SpecialtySelect>
            <Typography
              id="requestSubSpecialtyValidationdMessage"
              style={subSpecialtyRequiredErrorMessageStyle}>
              {t('Case.patient.field.required')}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
});

export default EconsultCaseSpecialtySection;
